import { ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MyBetsConfig, SitecoreMyBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { FreshDataService } from '@frontend/sports/data-refresh/feature';
import { CustomLoadingIndicatorService, LOADING_INDICATOR } from '@frontend/sports/loading-indicator-feature';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { TrackingService } from '@frontend/sports/tracking/feature';
import { BetslipListType } from '@frontend/sports/types/models/my-bets';
import { UserService } from '@frontend/sports/user/feature';
import { LocalStoreService } from '@frontend/vanilla/core';
import { switchMap, takeUntil } from 'rxjs';

import { EpcotConfigService, EpcotModule } from '../common/epcot-config.service';
import { GeolocationCheckService } from '../geo-location/geo-location-check.service';
import { HelpBoxService } from '../help-box/help-box.service';
import { ColumnLayoutService } from '../layout/column-layout.service';
import { MyBetsEarlyPayoutService } from '../my-bets-base/early-payout/my-bets-earlypayout.service';
import { MyBetsEarlyPayoutServiceMyBetsFactory } from '../my-bets-base/early-payout/my-bets-earlypayout.service.factory';
import { ExportButtonType, MigrationTabType, MyBetsListContext } from '../my-bets-base/models/my-bets-viewmodels';
import { PopupManager } from '../popup/popup-manager.service';
import { MyBetsListService } from './components/betslip-list/my-bets-list.service';
import { MyBetsMigrationTab, MyBetsTab } from './models/my-bets-tab';
import { MyBetsViewBaseComponent } from './my-bets-view-base.component';
import { AssociatedAccountService } from './services/associated-account-service';
import { MyBetsBetslipRepository } from './services/my-bets-betslip.repository';
import { MiniMyBetsStrategy, MyBetsStrategy } from './services/my-bets-display.strategy';
import { MyBetsEditBetHistoryService } from './services/my-bets-edit-bet-history.service';
import { MyBetsMigrationTabsService } from './services/my-bets-migration-tabs.service';
import { MyBetsTabsService } from './services/my-bets-tabs.service';
import { MyBetsUtilService } from './services/my-bets-util.service';
import { StadiumBetsVisibilityService } from './services/stadium-bets-visibility.service';

@Component({
    selector: 'ms-mini-my-bets-view',
    templateUrl: 'mini-my-bets-view.html',
    providers: [{ provide: MyBetsStrategy, useClass: MiniMyBetsStrategy }],
    standalone: false,
})
export class MiniMyBetsViewComponent extends MyBetsViewBaseComponent implements OnInit, OnDestroy {
    @Input() context = 'document';
    module = EpcotModule.Betslip;
    myBetsListContext = new MyBetsListContext();
    earlyPayoutService: MyBetsEarlyPayoutService;
    isEpcotEnabled = false;

    constructor(
        repository: MyBetsBetslipRepository,
        mybetsConfig: MyBetsConfig,
        editBetHistoryService: MyBetsEditBetHistoryService,
        userService: UserService,
        trackingService: TrackingService,
        helpBoxService: HelpBoxService,
        localStorage: LocalStoreService,
        loadingIndicatorService: CustomLoadingIndicatorService,
        earlyPayoutServiceFactory: MyBetsEarlyPayoutServiceMyBetsFactory,
        listService: MyBetsListService,
        public override sitecoreMyBets: SitecoreMyBetsConfig,
        public redirectHelperService: RedirectHelperService,
        private tabService: MyBetsTabsService,
        private freshData: FreshDataService,
        public override popupManager: PopupManager,
        associatedAccountService: AssociatedAccountService,
        private migrationTabService: MyBetsMigrationTabsService,
        private geolocationCheckService: GeolocationCheckService,
        stadiumMyBetsService: StadiumBetsVisibilityService,
        private epcotConfig: EpcotConfigService,
        protected override changeRef: ChangeDetectorRef,
        private columnLayoutService: ColumnLayoutService,
        @Optional()
        @Inject(LOADING_INDICATOR)
        token: string,
        public myBetsUtilService: MyBetsUtilService,
    ) {
        super(
            loadingIndicatorService,
            token,
            repository,
            mybetsConfig,
            helpBoxService,
            localStorage,
            editBetHistoryService,
            userService,
            trackingService,
            listService,
            true,
            sitecoreMyBets,
            popupManager,
            associatedAccountService,
            stadiumMyBetsService,
            changeRef,
        );

        this.myBetsListContext.isEventView = false;
        this.earlyPayoutService = earlyPayoutServiceFactory.create();
    }

    override async ngOnInit(): Promise<void> {
        super.ngOnInit();
        this.isEpcotEnabled = this.epcotConfig.isEnabled();
        if (this.userService.isAuthenticated) {
            this.geolocationCheckService.checkGeolocation();
        }
        await this.tryToReload();
        this.selectTab();

        if (this.showMigrationTabs) {
            this.selectMigrationTab();
            this.subscribeToMigrationTabSelected();
        }

        this.tabService.tabSelected
            .pipe(
                switchMap((tab) => this.onTabChange(tab)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();

        this.userService.onUserLogInWithSettings$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async () => {
            this.getAssociatedAccount();
            this.data.listType = undefined;
            await this.tryToReload();
            this.selectTab();
            if (this.showMigrationTabs) {
                this.selectMigrationTab();
                this.subscribeToMigrationTabSelected();
            }
            this.geolocationCheckService.checkGeolocation();
        });

        this.myBetsListContext.showMigrationTabs = this.showMigrationTabs;
        this.configureToolbarButtons();
        this.freshData.reloadNeeded.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.tryToReload());
    }

    private selectTab(): void {
        this.myBetsListContext = { ...this.myBetsListContext, listType: this.data.listType! };
        this.tabService.selectTab(this.data.listType!);
        this.showMoreVisible = this.data.hasMore;
        this.changeRef.detectChanges();
    }

    private onTabChange = async (tab: MyBetsTab) => {
        this.data.listType = tab.typeFilter;
        this.myBetsListContext.listType = tab.typeFilter;
        this.configureToolbarButtons();
        await this.tryToReload();
        this.changeRef.detectChanges();
    };

    private subscribeToMigrationTabSelected(): void {
        this.migrationTabService.tabSelected
            .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.userService.onUserLogin$))
            .subscribe(this.onMigrationTabChange);
    }

    private selectMigrationTab(): void {
        if (!this.migrationTabType) {
            this.migrationTabType = MigrationTabType.After;
        }
        this.myBetsListContext.migrationTabType = this.migrationTabType;
        this.migrationTabService.selectTab(this.migrationTabType);
    }

    private onMigrationTabChange = (tab: MyBetsMigrationTab) => {
        this.migrationTabType = tab.typeFilter;
        this.myBetsListContext.migrationTabType = tab.typeFilter;
        this.reload();
    };

    isShowMyBetsPopup(): boolean {
        return !this.columnLayoutService.layout.isRightVisible;
    }

    async tryToReload(): Promise<void> {
        if (!this.isAuthenticated) {
            return;
        }
        this.configureToolbarButtons();
        await this.reload();
    }

    private configureToolbarButtons(): void {
        const isSettled = this.myBetsListContext.listType === BetslipListType.Settled;
        this.myBetsListContext.showFilter = isSettled;
        if (isSettled) {
            const buttonType = this.mybetsConfig?.exportButtonType?.betSlip;
            if (buttonType) {
                this.myBetsListContext.showPrintButton = buttonType === ExportButtonType.Print;
                this.myBetsListContext.showDownloadButton = buttonType === ExportButtonType.Download;
            }
        }
    }

    ngOnDestroy(): void {
        if (this.epcotConfig.isShowMyBetsPopup) {
            this.tabService.resetTabs();
        }
    }
}
